export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly errorType: string,
    options?: { cause?: unknown },
  ) {
    super(message, options);
    this.name = 'ApiError';
  }
}

export class BadRequestError extends ApiError {
  constructor(message: string, errorType: string) {
    super(message, 400, errorType);
    this.name = 'BadRequestError';
  }
}

export class NotAuthenticatedError extends ApiError {
  constructor(message: string, errorType: string) {
    super(message, 401, errorType);
    this.name = 'NotAuthenticatedError';
  }
}

export class NotAuthorizedError extends ApiError {
  constructor(message: string, errorType: string) {
    super(message, 403, errorType);
    this.name = 'NotAuthorizedError';
  }
}

export class NotFoundError extends ApiError {
  constructor(message: string, errorType: string) {
    super(message, 404, errorType);
    this.name = 'NotFoundError';
  }
}

export class ConflictError extends ApiError {
  constructor(message: string, errorType: string) {
    super(message, 409, errorType);
    this.name = 'ConflictError';
  }
}

export class ValidationError extends ApiError {
  constructor(message: string, errorType: string) {
    super(message, 422, errorType);
    this.name = 'ValidationError';
  }
}

export function isSdkError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

// Transforms hey-api raw errors (RFC 7807 shape) into typed subclasses.
// body is the parsed response body; httpStatus is the authoritative HTTP status from Response.status.
export function rethrow(body: unknown, httpStatus: number): never {
  const raw = body && typeof body === 'object' ? (body as { type?: string; title?: string }) : {};
  const type = raw.type ?? 'urn:app:error:unknown';
  const title = raw.title ?? 'An error occurred';

  switch (httpStatus) {
    case 400:
      throw new BadRequestError(title, type);
    case 401:
      throw new NotAuthenticatedError(title, type);
    case 403:
      throw new NotAuthorizedError(title, type);
    case 404:
      throw new NotFoundError(title, type);
    case 409:
      throw new ConflictError(title, type);
    case 422:
      throw new ValidationError(title, type);
    default:
      throw new ApiError(title, httpStatus, type);
  }
}

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: ApiError;
  }
}

export function extractErrorMessage(
  err: unknown,
  fallback = 'An error occurred. Please try again.',
): string {
  const e = err as
    | { data?: { detail?: string; message?: string }; message?: string }
    | null
    | undefined;
  return e?.data?.detail || e?.data?.message || e?.message || fallback;
}
